// src/lib/master-engine-input.ts
// Ein einziger Normalizer für Inputs aus Store/Formular

import type { EngineInput } from "@/lib/master-engine";
import { mapWizardAnswersToDiagnosticIds } from "@/lib/master-wizard-to-diagnostic-mapper";

export type CompanyProfile = {
  companyName?: string;
  industry?: string;
  businessModel?: string;
  teamSize?: string | number;
  revenue?: number;
  acquisitionMethods?: Record<string, number>;
  leadSources?: string[];
  funnelData?: {
    monthlyContacts?: number;
    monthlyDiscoveryCalls?: number;
    monthlyPresentations?: number;
    monthlyDeals?: number;
    averageDealSize?: number;
    monthlyRevenue?: number;
    salesCycleMonths?: number;
    responseTime?: string;
    followUpDiscipline?: string;
    pipelineTransparency?: string;
    leadQuality?: string;
  };
  [key: string]: unknown; // Allow additional properties
};

export type AnswersMap = Record<string, unknown>;

export function buildEngineInput(
  answers: AnswersMap,
  companyProfile: CompanyProfile,
): EngineInput {
  // Store original values for unknown field detection
  const originalFunnelData = companyProfile?.funnelData;
  
  // CRITICAL FIX: Map wizard answers to diagnostic IDs
  // This ensures calculateDiagnosticScoring gets real answers instead of defaults
  const diagnosticAnswers = mapWizardAnswersToDiagnosticIds(answers);
  
  // Merge original answers with diagnostic mappings
  const mergedAnswers = { ...answers, ...diagnosticAnswers };
  
  return {
    answers: Object.entries(mergedAnswers || {}).map(([questionId, value]) => ({
      questionId,
      value,
    })),
    funnelData: companyProfile?.funnelData
      ? {
          // CRITICAL: pass null as-is so the engine can distinguish
          // "not provided" from "provided as 0".
          // Only hard-fallback scalars that are ALWAYS required (salesCycleMonths, strings).
          monthlyContacts: companyProfile.funnelData.monthlyContacts ?? null,
          monthlyDiscoveryCalls: companyProfile.funnelData.monthlyDiscoveryCalls ?? null,
          monthlyPresentations: companyProfile.funnelData.monthlyPresentations ?? null,
          monthlyDeals: companyProfile.funnelData.monthlyDeals ?? null,
          averageDealSize: companyProfile.funnelData.averageDealSize ?? null,
          monthlyRevenue: companyProfile.funnelData.monthlyRevenue ?? null,
          salesCycleMonths: companyProfile.funnelData.salesCycleMonths ?? 3,
          responseTime: companyProfile.funnelData.responseTime || "not-known",
          followUpDiscipline: companyProfile.funnelData.followUpDiscipline || "not-known",
          pipelineTransparency: companyProfile.funnelData.pipelineTransparency || "not-known",
          leadQuality: companyProfile.funnelData.leadQuality || "not-known",
        }
      : {
          monthlyContacts: null,
          monthlyDiscoveryCalls: null,
          monthlyPresentations: null,
          monthlyDeals: null,
          averageDealSize: null,
          monthlyRevenue: null,
          salesCycleMonths: 3,
          responseTime: "not-known",
          followUpDiscipline: "not-known",
          pipelineTransparency: "not-known",
          leadQuality: "not-known",
        },
    companyContext: {
      companyName: companyProfile?.companyName,
      industry: companyProfile?.industry,
      businessModel: companyProfile?.businessModel,
      salesMotion: undefined,
      teamSize:
        Number.parseInt(String(companyProfile?.teamSize ?? ""), 10) || undefined,
      acquisitionMethods: companyProfile?.acquisitionMethods,
    },
    // Pass original data for unknown field detection
    _originalFunnelData: originalFunnelData,
    _originalCompanyProfile: companyProfile,
  };
}